import defaultPsychologistImage from '@avanta/shared/assets/avatar.svg';
import { Text } from '@avanta/shared/components/Text';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { formatTime, formatUnifiedDateWithMonth } from '@avanta/shared/utils/date';
import { Building, Calendar, ClipboardPenLine, Clock, IdCard, MapPin, User } from 'lucide-react';
import type * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { InfoItem } from 'components/InfoItem';

const SUMMARY_LABEL_CLASS_NAME = 'text-denim-800 wrap-break-words';

export const AppointmentSummary = (): React.ReactElement => {
  const { state } = useLocation();
  const { t } = useTranslation(LOCIZE_NS.APPOINTMENTS);
  const { patient, psychologist, center } = state;
  const specialityLabel = t(
    `AppointmentsReserve.Steps.speciality.options.${state?.specialitiesBookable}`
  );
  const patientName = `${patient?.firstName} ${patient?.surname}`;

  return (
    <div className="flex w-full max-w-191.5 flex-col rounded-xl border border-teal-300 bg-white px-4 pt-4 pb-6 am-xl:px-6 am-xl:pb-8 [&_svg]:stroke-[1.25]">
      <div className="mb-4 flex flex-col gap-4 am-xl:flex-row am-xl:items-center am-xl:justify-between">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
          <InfoItem
            icon={Calendar}
            label={formatUnifiedDateWithMonth(state?.dateTime)}
            labelClassName="text-base font-medium leading-5 text-denim-800"
          />
          <InfoItem
            icon={Clock}
            label={formatTime(state?.dateTime)}
            labelClassName="text-base leading-5 text-denim-800"
          />
        </div>
        <div className="flex min-w-0 items-center gap-2">
          <img
            src={psychologist.imageCircular || defaultPsychologistImage}
            alt=""
            className="size-10 shrink-0 rounded-full object-cover"
          />
          <div className="flex min-w-0 flex-col gap-1">
            <Text variant="text-xs-regular" className="text-gray-400">
              {t('AppointmentsReserve.Steps.confirmation.licensed.label', {
                professionalNumber: psychologist.professionalNumber,
              })}
            </Text>
            <Text variant="text-sm-medium" className={SUMMARY_LABEL_CLASS_NAME}>
              {psychologist.name}
            </Text>
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-5 rounded-xl bg-mint-100 p-4">
        <div className="flex flex-col gap-2">
          <Text variant="text-sm-medium" className="text-denim-800 ">
            {t('AppointmentsSuccess.patient.label', { defaultValue: 'Paciente:' })}
          </Text>
          <div className="grid min-w-0 grid-cols-1 gap-2 am-xl:grid-cols-2 am-xl:gap-x-8">
            <InfoItem icon={User} label={patientName} labelClassName={SUMMARY_LABEL_CLASS_NAME} />
            <InfoItem icon={IdCard} label={patient.nif} labelClassName={SUMMARY_LABEL_CLASS_NAME} />
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <Text variant="text-sm-medium" className={SUMMARY_LABEL_CLASS_NAME}>
            {t('AppointmentsSuccess.service.label', { defaultValue: 'Servicio:' })}
          </Text>
          <div className="grid min-w-0 grid-cols-1 gap-4 am-xl:grid-cols-2 am-xl:gap-x-8">
            <div className="flex min-w-0 flex-col gap-2">
              <InfoItem
                icon={ClipboardPenLine}
                label={specialityLabel}
                labelClassName={SUMMARY_LABEL_CLASS_NAME}
              />
              <InfoItem
                icon={Building}
                label={t('AppointmentsReserve.Steps.confirmation.contactMethod.in-person', {
                  duration: state?.durationInMinutes,
                })}
                labelClassName={SUMMARY_LABEL_CLASS_NAME}
              />
            </div>
            <div className="flex min-w-0 flex-col gap-2">
              <InfoItem
                icon={MapPin}
                label={center?.city}
                labelClassName={SUMMARY_LABEL_CLASS_NAME}
              />
              <div className="max-w-58">
                <InfoItem
                  icon={Building}
                  label={center?.label}
                  description={center?.description}
                  labelClassName={SUMMARY_LABEL_CLASS_NAME}
                  alignStart
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
